/**
 * Account Transfer Request Popup
 * 
 * Shows when someone wants to transfer their account to you
 */

import React, { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, Building2, Mail, FileText, Shield } from 'lucide-react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { AccountTransferRequest } from '../types/accountTransfer.types';
import { acceptTransferRequest, declineTransferRequest } from '../services/accountTransferService';

interface AccountTransferRequestPopupProps {
  userId: string;
  onClose?: () => void;
}

export const AccountTransferRequestPopup: React.FC<AccountTransferRequestPopupProps> = ({
  userId,
  onClose
}) => {
  const [requests, setRequests] = useState<AccountTransferRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [currentRequestIndex, setCurrentRequestIndex] = useState(0);

  useEffect(() => {
    const requestsRef = collection(db, 'accountTransfers');
    const q = query(
      requestsRef,
      where('newOwnerId', '==', userId),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const pendingRequests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AccountTransferRequest[];
      
      setRequests(pendingRequests);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  const currentRequest = requests[currentRequestIndex];

  const handleAccept = async () => {
    if (!verificationCode.trim()) {
      alert('⚠️ Vul de verificatiecode in.');
      return;
    }

    if (processing) return;

    try {
      setProcessing(true);
      await acceptTransferRequest(currentRequest.id, userId, verificationCode);
      
      alert('✅ Overdracht geaccepteerd! Je bent nu de eigenaar van dit bedrijf.');
      
      // Remove from list
      setRequests(prev => prev.filter(r => r.id !== currentRequest.id));
      setShowAcceptModal(false);
      setVerificationCode('');
      
      if (requests.length === 1) {
        onClose?.();
      }
    } catch (error: any) {
      alert(`❌ Fout: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      alert('⚠️ Geef een reden voor afwijzing.');
      return;
    }

    if (processing) return;

    try {
      setProcessing(true);
      await declineTransferRequest(currentRequest.id, userId, declineReason);
      
      alert('✅ Overdracht afgewezen.');
      
      // Remove from list
      setRequests(prev => prev.filter(r => r.id !== currentRequest.id));
      setShowDeclineModal(false);
      setDeclineReason('');
      
      if (requests.length === 1) {
        onClose?.();
      }
    } catch (error: any) {
      alert(`❌ Fout: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return null;
  }

  if (showDeclineModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 max-w-md w-full">
          <h3 className="text-xl font-bold mb-4">Overdracht Afwijzen</h3>
          <textarea
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            rows={4}
            placeholder="Geef een reden..."
            className="w-full px-4 py-2 border rounded-lg mb-4"
          />
          <div className="flex space-x-3">
            <button
              onClick={() => setShowDeclineModal(false)}
              className="flex-1 px-4 py-2 border rounded-lg"
            >
              Annuleren
            </button>
            <button
              onClick={handleDecline}
              disabled={processing}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg"
            >
              Afwijzen
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showAcceptModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 max-w-md w-full">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
            <h3 className="text-xl font-bold">Verificatiecode</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Voer de 6-cijferige verificatiecode in die je hebt ontvangen van de huidige eigenaar.
          </p>
          <input
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="123456"
            maxLength={6}
            className="w-full px-4 py-3 border rounded-lg text-center text-2xl font-mono mb-4"
          />
          <p className="text-xs text-gray-500 mb-4">
            Code: <strong>{currentRequest.verificationCode}</strong> (Neem contact op met de eigenaar voor deze code)
          </p>
          <div className="flex space-x-3">
            <button
              onClick={() => {
                setShowAcceptModal(false);
                setVerificationCode('');
              }}
              className="flex-1 px-4 py-2 border rounded-lg"
            >
              Annuleren
            </button>
            <button
              onClick={handleAccept}
              disabled={processing || verificationCode.length !== 6}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50"
            >
              Accepteren
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 rounded-t-xl">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <AlertTriangle className="w-6 h-6" />
                <h2 className="text-2xl font-bold">Account Overdracht Aanvraag</h2>
              </div>
              <p className="text-orange-100">
                Iemand wil hun bedrijf aan jou overdragen
              </p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Request Counter */}
          {requests.length > 1 && (
            <div className="flex justify-center space-x-2">
              {requests.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === currentRequestIndex ? 'bg-orange-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Warning */}
          <div className="p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
            <h4 className="font-bold text-orange-900 mb-2 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Belangrijke Beslissing
            </h4>
            <p className="text-sm text-orange-800 mb-2">
              Als je deze overdracht accepteert, word je de volledige eigenaar van dit bedrijfsaccount. 
            </p>
            <p className="text-sm text-orange-800 font-semibold">
              Je krijgt het <strong>COMPLETE account</strong> met ALLE data, instellingen, en historie. 
              Alleen de naam wordt aangepast naar jouw gegevens.
            </p>
          </div>

          {/* From (Current Owner) */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Van (Huidige Eigenaar)</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center">
                <Mail className="w-4 h-4 text-gray-400 mr-3" />
                <span className="font-medium">{currentRequest.currentOwnerEmail}</span>
              </div>
              <div className="flex items-center">
                <Building2 className="w-4 h-4 text-gray-400 mr-3" />
                <span>{currentRequest.companyName}</span>
              </div>
            </div>
          </div>

          {/* Transfer Details */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Overdracht Details</h3>
            <div className="bg-blue-50 rounded-lg p-4 space-y-3">
              <div>
                <p className="text-sm text-blue-700">Reden</p>
                <p className="font-medium text-blue-900">{currentRequest.transferReason}</p>
              </div>
              {currentRequest.notes && (
                <div>
                  <p className="text-sm text-blue-700">Opmerkingen</p>
                  <p className="text-blue-900">{currentRequest.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* What's Included */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">✅ Complete Account Overdracht</h3>
            <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
              <p className="text-sm text-green-800 mb-3 font-semibold">
                Het VOLLEDIGE account wordt overgedragen, inclusief:
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm text-green-800">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Profiel & Instellingen
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Managers, VA's & Board
                </div>
                {currentRequest.includesEntities.length > 0 && (
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {currentRequest.includesEntities.length} Entities
                  </div>
                )}
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Doelstellingen & Goals
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Content & Posts
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Connecties & Netwerk
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Agenda & Calendar
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Time Tracking
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Chat Historie
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Analytics & Stats
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Notificaties & Inbox
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Expert Status
                </div>
              </div>
              <p className="text-xs text-green-700 mt-3 italic">
                💡 Alles blijft zoals het was, alleen de naam wordt aangepast naar jouw gegevens
              </p>
            </div>
          </div>

          {/* Verification Code Info */}
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>Verificatie vereist:</strong> Je hebt een 6-cijferige code nodig van de huidige eigenaar 
              om deze overdracht te accepteren. Dit zorgt ervoor dat beide partijen het eens zijn.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-50 p-6 rounded-b-xl border-t flex space-x-3">
          <button
            onClick={() => setShowDeclineModal(true)}
            disabled={processing}
            className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50"
          >
            <XCircle className="w-5 h-5" />
            <span>Afwijzen</span>
          </button>
          <button
            onClick={() => setShowAcceptModal(true)}
            disabled={processing}
            className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <CheckCircle className="w-5 h-5" />
            <span>Accepteren</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountTransferRequestPopup;
